/**
 * Every angle a scan measured, for the clinic's dashboard.
 *
 * The patient list only carries the verdicts (arch type, "Overpronation"),
 * which say nothing about how far out a reading was or which view it came
 * from. This lays out the numbers the report is built on — each view and leg
 * pose with its angles in degrees, the reference range and the grade — so a
 * clinician can read them without opening the report.
 *
 * Only the sections the clinic was given (limitScanToSections), as on the
 * report itself. The grades come from metricBands.js, as the report's do.
 */

import { rangeForAngle, severityForAngle } from '../metricBands';
import {
  HEEL_TEXT, SHIN_TEXT, isUnmeasured, legPosesOf, postureViewsOf, visionReadingOf,
} from './scanModel';
import { cameraFootSizeRows, staticPlantarRows, walkRows, walkTestOf } from './plantarModel';
import { limitScanToSections } from './reportSections';
import { referenceText } from './reference';

export const SEVERITY_TEXT = { normal: 'Normal', mild: 'Mild', moderate: 'Moderate', severe: 'Severe' };

/** The walking test's rows the dashboard shows: the angle, and what it says of the arch. */
const WALK_ROWS = ['Foot progression angle (Fick)', 'Arch index', 'Forefoot / rearfoot (F/R) ratio', 'Stance time'];

/** A left/right row the scan has a figure for; a row of dashes says nothing. */
const measuredOnEitherFoot = r => r.left !== '—' || r.right !== '—';

function angleRow(angle) {
  const measured = Number.isFinite(angle.degrees);
  return {
    name: angle.name,
    degrees: measured ? angle.degrees : null,
    range: rangeForAngle(angle),
    severity: measured ? severityForAngle(angle) : null,
  };
}

/** The leg findings as sentences: "Left heel: Valgus — heel tilts inward (7.5°)". */
function legFindings(findings) {
  if (!findings) return [];
  const line = (label, f, text) => {
    if (!f) return null;
    const deg = Number.isFinite(f.degrees) ? ` (${f.degrees.toFixed(1)}°)` : '';
    return `${label}: ${text[f.finding] ?? f.finding}${deg}`;
  };
  return [
    line('Left heel', findings.heelLeft, HEEL_TEXT),
    line('Right heel', findings.heelRight, HEEL_TEXT),
    line('Left shin', findings.shinLeft, SHIN_TEXT),
    line('Right shin', findings.shinRight, SHIN_TEXT),
  ].filter(Boolean);
}

function captureGroup(capture, kind, findings = []) {
  const title = capture.label ?? capture.view ?? capture.pose;
  if (isUnmeasured(capture)) return { title, kind, unmeasured: true, rows: [], findings: [] };
  const rows = (capture.angles ?? []).filter(a => a?.name).map(angleRow);
  return { title, kind, unmeasured: false, rows, findings };
}

/**
 * The scan's measurements, grouped by where they were taken.
 *
 * `groups` holds one entry per body view and leg pose, each with its angle
 * rows; a capture whose joints could not be read is kept, marked unmeasured,
 * so it does not look like a view that was never taken. `foot` holds the
 * classification and the per-foot plantar figures, or null for a scan without
 * them; `walk` the walking test's angle and arch rows, or null. `measured`
 * and `outside` count the angles, for the summary line.
 */
export function scanDegreesOf(storedScan) {
  const scan = limitScanToSections(storedScan);
  if (!scan) return { groups: [], foot: null, walk: null, measured: 0, outside: 0 };

  const groups = [
    ...postureViewsOf(scan).map(v => captureGroup(v, 'posture')),
    ...legPosesOf(scan).map(p => captureGroup(p, 'leg', legFindings(p.findings))),
  ].filter(g => g.unmeasured || g.rows.length || g.findings.length);

  const readings = groups.flatMap(g => g.rows).filter(r => r.degrees !== null);
  const outside = readings.filter(r => r.severity && r.severity !== 'normal').length;

  const plantar = [
    ...(staticPlantarRows(scan.sensorAnalytics)?.rows ?? []),
    ...(cameraFootSizeRows(scan.footSize, scan.sensorAnalytics) ?? []),
  ].filter(measuredOnEitherFoot);
  const classification = [
    ['Medial arch', scan.medialArchType || scan.archVisionAnalysis?.archHeight],
    ['Foot type', scan.footType || scan.archVisionAnalysis?.pronationStatus],
    ['AI vision reading', visionReadingOf(scan)],
  ].filter(([, value]) => value);
  const foot = plantar.length || classification.length ? { classification, plantar } : null;

  const walkTest = walkTestOf(scan);
  const walk = walkTest && (walkTest.left || walkTest.right)
    ? walkRows(walkTest).filter(r => WALK_ROWS.includes(r.label) && measuredOnEitherFoot(r))
    : null;

  return { groups, foot, walk, measured: readings.length, outside };
}

/** An angle as the dashboard prints it. */
export function formatDegrees(degrees) {
  return degrees === null || degrees === undefined ? 'Not measured' : `${degrees.toFixed(1)}°`;
}

/** The reference, as the report states it: "0° (normal up to 3°)", or the range (lib/report/reference). */
export function formatRange(range) {
  return referenceText(range);
}
